/**
 * Dictation anywhere settings saved before prompts were chosen at stop time.
 * Then, "Improve with AI" applied every switched-on prompt together; now a
 * stop applies one. What was switched on becomes Default's text, in the order
 * it was applied (Clean up, Make concise, the user's own prompts, Translate
 * last), and "Improve with AI" becomes Default's switch. The user's own
 * prompts become numbered prompts 1 to 9, ids kept.
 *
 * The only place that knows the old field names; the store parses the result.
 */
import {
  MAX_PROMPTS,
  PROMPT_NAME_MAX,
  PROMPT_TEXT_MAX,
} from "@/desktop/dictation-anywhere/polish-prompts";
import {
  CLEAN_UP_SENTENCE,
  MAKE_CONCISE_SENTENCE,
  translateSentence,
} from "@/desktop/dictation-anywhere/prompt-chips";
import type { DictationAnywhereSettings } from "./dictation-anywhere-settings-store";

const CLEAN_UP_ID = "builtin.clean-up";
const MAKE_CONCISE_ID = "builtin.make-concise";
const TRANSLATE_ID = "builtin.translate";
/** The old default: Clean up switched on, AI off. */
const LEGACY_ENABLED_DEFAULT = [CLEAN_UP_ID];
const LEGACY_LANGUAGE_DEFAULT = "English";
const LANGUAGE_PLACEHOLDER = "{language}";
const LANGUAGE_MAX = 64;

type LegacyPrompt = { id: string; name: string; instruction: string };

/** Settings in the old shape, and not yet in the new one. */
export function isLegacyDictationAnywhereSettings(
  saved: Record<string, unknown>,
): boolean {
  return (
    ("polishEnabled" in saved || "enabledPromptIds" in saved) &&
    !("defaultPrompt" in saved)
  );
}

const isString = (value: unknown): value is string => typeof value === "string";

function legacyCustomPrompts(value: unknown): LegacyPrompt[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  return value.flatMap((entry) => {
    if (!entry || typeof entry !== "object") return [];
    const { id, name, instruction } = entry as Record<string, unknown>;
    if (!isString(id) || !id || seen.has(id)) return [];
    if (!isString(name) || !isString(instruction)) return [];
    seen.add(id);
    return [{ id, name, instruction }];
  });
}

/** The user's edit of a built-in's instruction, or null. */
function editedInstruction(overrides: unknown, id: string): string | null {
  if (!overrides || typeof overrides !== "object") return null;
  const edit = (overrides as Record<string, unknown>)[id];
  if (!edit || typeof edit !== "object") return null;
  const { instruction } = edit as Record<string, unknown>;
  return isString(instruction) && instruction.trim() ? instruction : null;
}

function legacyLanguage(value: unknown): string {
  return isString(value) && value.trim() && value.length <= LANGUAGE_MAX
    ? value.trim()
    : LEGACY_LANGUAGE_DEFAULT;
}

export function migrateLegacyDictationAnywhereSettings(
  saved: Record<string, unknown>,
): DictationAnywhereSettings {
  const enabled = new Set(
    Array.isArray(saved.enabledPromptIds)
      ? saved.enabledPromptIds.filter(isString)
      : LEGACY_ENABLED_DEFAULT,
  );
  const customs = legacyCustomPrompts(saved.customPrompts);
  const language = legacyLanguage(saved.translateLanguage);
  const builtin = (id: string, sentence: string) =>
    enabled.has(id)
      ? [editedInstruction(saved.builtinOverrides, id) ?? sentence]
      : [];
  const translate = enabled.has(TRANSLATE_ID)
    ? [
        editedInstruction(saved.builtinOverrides, TRANSLATE_ID)
          ?.split(LANGUAGE_PLACEHOLDER)
          .join(language) ?? translateSentence(language),
      ]
    : [];
  const instruction = [
    ...builtin(CLEAN_UP_ID, CLEAN_UP_SENTENCE),
    ...builtin(MAKE_CONCISE_ID, MAKE_CONCISE_SENTENCE),
    ...customs
      .filter((prompt) => enabled.has(prompt.id))
      .map((prompt) => prompt.instruction),
    ...translate,
  ]
    .map((text) => text.trim())
    .filter(Boolean)
    .join("\n")
    .slice(0, PROMPT_TEXT_MAX);
  return {
    autoPaste: saved.autoPaste !== false,
    defaultPrompt: { enabled: saved.polishEnabled === true, instruction },
    prompts: customs.slice(0, MAX_PROMPTS).map(({ id, name, instruction }) => ({
      id,
      name: name.slice(0, PROMPT_NAME_MAX),
      instruction: instruction.slice(0, PROMPT_TEXT_MAX),
    })),
  };
}
